import React, { useEffect, useState } from 'react';
import {
  doc,
  getDoc,
  getDocs,
  collection,
  query,
  orderBy,
  addDoc,
} from 'firebase/firestore';
import { auth, db } from './config/firebaseConfig';
import { onAuthStateChanged } from 'firebase/auth';
import './YaleBrownForm.css';
import Navbar from './Navbar';
import Footer from './Footer';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import BookPromo from './BookPromo';
import bookCover from './book-cover.jpg';
import MobileApps from './MobileApps';

/* =============================================================================
   QUESTIONNAIRE CONFIGURATION
   =============================================================================
   IMPORTANT — READ BEFORE EDITING:

   This page no longer contains the Y-BOCS. The Y-BOCS is copyright
   Goodman et al. / OCD Scales, Inc. and may not be posted, digitized, or
   distributed online. Clinicians who want the Y-BOCS should contact
   dr.g@ocdscales.com (see the notice rendered further down this page).

   Do NOT paste any questionnaire items into ITEMS below until you have
   WRITTEN permission from that instrument's copyright holder covering
   web/digital use on this site. Keep a copy of that permission on file.

   Suggested replacement: the OCI-R (Obsessive-Compulsive Inventory –
   Revised, Foa et al., 2002). It is free for non-commercial use, but you
   should email Dr. Foa's team for written confirmation that hosting it on
   this page is acceptable, describing the page exactly (including any book
   or app promotion, or after removing it). Once you have permission, add
   the 18 items here and the form activates automatically.

   Each item: { key: 'uniqueKey', text: 'Item text from the licensed scale' }
   ========================================================================== */
const QUESTIONNAIRE = {
  id: 'OCIR_v1', // stored with each saved result, so old Y-BOCS history stays separate
  name: 'OCD Symptom Screening (OCI-R)',
  shortName: 'OCI-R',
  // Standard 0–4 distress response options used by the OCI-R.
  responseOptions: [
    { label: 'Not at all', value: 0 },
    { label: 'A little', value: 1 },
    { label: 'Moderately', value: 2 },
    { label: 'A lot', value: 3 },
    { label: 'Extremely', value: 4 },
  ],
  // Total ranges 0–72 across 18 items. A total of 21 or higher is the
  // commonly used screening cut-off suggesting likely OCD (not a diagnosis).
  maxScore: 72,
  cutoff: 21,
  citation:
    'Foa, E. B., Huppert, J. D., Leiberg, S., Langner, R., Kichic, R., Hajcak, G., & Salkovskis, P. M. (2002). The Obsessive-Compulsive Inventory: Development and validation of a short version. Psychological Assessment, 14(4), 485–496.',
  // ⚠️ EMPTY until you have written permission — see the note above.
  items: [
    // { key: 'item1', text: '...' },
    // { key: 'item2', text: '...' },
    // ... 18 items total for the OCI-R
  ],
};

// Supportive interpretation relative to the screening cut-off.
const getScoreBand = total => {
  if (total < QUESTIONNAIRE.cutoff) {
    return {
      label: 'Below screening threshold',
      summary:
        'Your score is below the commonly used screening threshold for OCD.',
      nextSteps:
        'A score below the threshold does not rule anything out. If obsessions or compulsions still affect your daily life, tracking your score over time and talking with a clinician can help you see the bigger picture.',
    };
  }
  return {
    label: 'Above screening threshold',
    summary:
      'Your score is at or above the commonly used screening threshold, which suggests symptoms worth discussing with a professional.',
    nextSteps:
      'This is a screening result, not a diagnosis. Bringing this result to a therapist or doctor is a good next step — effective treatments for OCD exist, and the right support can make a real difference.',
  };
};

const OCDScreeningForm = () => {
  const [userRole, setUserRole] = useState('');
  const [scoreHistory, setScoreHistory] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const currentUser = auth.currentUser;
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [touched, setTouched] = useState(() => new Set());
  const [answers, setAnswers] = useState({});
  const [successMessage, setSuccessMessage] = useState('');

  const scaleReady = QUESTIONNAIRE.items.length > 0;

  // Recalculate total whenever answers change.
  useEffect(() => {
    const sum = QUESTIONNAIRE.items.reduce(
      (acc, item) => acc + (answers[item.key] || 0),
      0
    );
    setTotal(sum);
  }, [answers]);

  // Auth state + role.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async user => {
      if (user) {
        setIsLoggedIn(!!user);
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          setUserRole(userDoc.data().role);
        }
      }
    });
    return () => unsubscribe();
  }, []);

  // Fetch saved forms (both legacy Y-BOCS entries and new-scale entries).
  const fetchForms = async () => {
    if (!currentUser) return;
    const userDocRef = doc(db, 'YBOCS_assessment', currentUser.uid);
    const formsCollectionRef = collection(userDocRef, 'YBOCS_forms');
    try {
      const q = query(formsCollectionRef, orderBy('timestamp', 'asc'));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const formsData = [];
        querySnapshot.forEach(d => {
          const data = d.data();
          formsData.push({
            timestamp: data.timestamp.toDate(),
            totalScore:
              data.scale === QUESTIONNAIRE.id
                ? data.total
                : data.scores?.total ?? data.total ?? 0,
            scale: data.scale || 'YBOCS_legacy',
          });
        });
        setScoreHistory(formsData);
      }
    } catch (error) {
      console.error('Error fetching forms:', error);
    }
  };

  useEffect(() => {
    const load = async () => {
      await fetchForms();
      setLoading(false);
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Anonymous users: load local history.
  useEffect(() => {
    if (!currentUser) {
      try {
        const stored = localStorage.getItem('OCD_screening_history');
        if (stored) {
          const parsed = JSON.parse(stored);
          setScoreHistory(
            parsed.map(h => ({ ...h, timestamp: new Date(h.timestamp) }))
          );
        }
      } catch (e) {
        console.error('Error loading local history:', e);
      }
    }
  }, [currentUser]);

  const handleChange = (e, key) => {
    const { value } = e.target;
    setTouched(prev => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    setAnswers(prev => ({ ...prev, [key]: parseInt(value, 10) }));
  };

  const handleReset = () => {
    const confirmed = window.confirm(
      'Start over and clear all your current answers? This will not delete any results you have already saved.'
    );
    if (!confirmed) return;
    setAnswers({});
    setTouched(new Set());
    setSuccessMessage('');
  };

  // Printable clinician summary (browser print dialog handles "Save as PDF").
  const handleExport = () => {
    const band = getScoreBand(total);
    const dateStr = new Date().toLocaleDateString();
    const escapeHtml = str =>
      String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

    const rows = QUESTIONNAIRE.items
      .map(item => {
        const val = typeof answers[item.key] === 'number' ? answers[item.key] : 0;
        return `<tr><td>${escapeHtml(item.text)}</td><td style="text-align:center">${val} / 4</td></tr>`;
      })
      .join('');

    const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>${escapeHtml(QUESTIONNAIRE.name)} Summary — ${dateStr}</title>
<style>
  body { font-family: Arial, Helvetica, sans-serif; color: #222; max-width: 800px; margin: 24px auto; padding: 0 16px; line-height: 1.5; }
  h1 { font-size: 1.5rem; margin-bottom: 4px; }
  h2 { font-size: 1.15rem; margin-top: 28px; border-bottom: 1px solid #ccc; padding-bottom: 4px; }
  table { width: 100%; border-collapse: collapse; margin-top: 8px; }
  td { padding: 6px 8px; border-bottom: 1px solid #eee; }
  .num { font-size: 1.6rem; font-weight: bold; }
  .disclaimer { margin-top: 28px; font-size: 0.8rem; color: #666; border-top: 1px solid #ccc; padding-top: 10px; }
</style>
</head>
<body>
  <h1>${escapeHtml(QUESTIONNAIRE.name)}</h1>
  <p>Self-report summary &middot; ${dateStr}</p>
  <h2>Total Score</h2>
  <p><span class="num">${total}</span> / ${QUESTIONNAIRE.maxScore} &mdash; ${escapeHtml(band.label)}</p>
  <h2>Item Scores (0–4)</h2>
  <table>${rows}</table>
  <div class="disclaimer">
    This is a self-administered screening and self-tracking summary. It is not a
    diagnosis and is not a substitute for professional clinical assessment.<br/>
    ${escapeHtml(QUESTIONNAIRE.citation)}
  </div>
</body>
</html>`;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow pop-ups for this site to download or print your summary.');
      return;
    }
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 300);
  };

  const handleSubmit = async e => {
    e.preventDefault();

    const entry = {
      scale: QUESTIONNAIRE.id,
      answers,
      total,
      timestamp: new Date(),
    };

    if (currentUser) {
      const userDocRef = doc(db, 'YBOCS_assessment', currentUser.uid);
      const formsCollectionRef = collection(userDocRef, 'YBOCS_forms');
      try {
        await addDoc(formsCollectionRef, entry);
        alert('Your assessment has been saved successfully!');
        fetchForms();
      } catch (error) {
        console.error('Error saving assessment:', error);
        alert('There was an error saving your assessment. Please try again.');
      }
    } else {
      const newEntry = { ...entry, timestamp: new Date().toISOString(), totalScore: total };
      let history = [];
      try {
        const stored = localStorage.getItem('OCD_screening_history');
        if (stored) history = JSON.parse(stored);
      } catch (err) {
        history = [];
      }
      history.push(newEntry);
      localStorage.setItem('OCD_screening_history', JSON.stringify(history));
      setScoreHistory(
        history.map(h => ({ ...h, timestamp: new Date(h.timestamp) }))
      );
      setSuccessMessage(
        'Your results have been saved on this device. Create a free account to back them up and access them from any device.'
      );
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  const band = getScoreBand(total);
  const answeredCount = QUESTIONNAIRE.items.filter(i => touched.has(i.key)).length;

  // Chart only entries from the current scale (legacy Y-BOCS scores use a
  // different range and would make the graph misleading).
  const currentScaleHistory = scoreHistory.filter(
    h => h.scale === QUESTIONNAIRE.id
  );
  const legacyHistory = scoreHistory.filter(h => h.scale !== QUESTIONNAIRE.id);

  let trend = null;
  if (currentScaleHistory.length >= 2) {
    const latest = currentScaleHistory[currentScaleHistory.length - 1];
    const previous = currentScaleHistory[currentScaleHistory.length - 2];
    const delta = (latest.totalScore ?? latest.total) - (previous.totalScore ?? previous.total);
    trend = { delta, direction: delta < 0 ? 'down' : delta > 0 ? 'up' : 'same' };
  }

  return (
    <>
      <Navbar />
      <div className='yale-brown-form'>
        <h1>OCD Symptom Screening</h1>
        <p className='assessment-intro'>
          This page offers a brief, validated self-report screening for
          obsessive-compulsive symptoms. It helps you understand the impact of
          obsessions and compulsions on daily life and track changes over time.
        </p>

        {/* Notice for visitors looking for the Y-BOCS */}
        <div
          className='assessment-intro'
          style={{
            backgroundColor: '#2e2e2e',
            border: '1px solid #4a4a4a',
            borderLeft: '4px solid #e0c97a',
            borderRadius: '10px',
            padding: '18px 22px',
            margin: '20px auto',
            maxWidth: '700px',
            textAlign: 'left',
            fontSize: '0.95rem',
            lineHeight: '1.5',
          }}
        >
          <strong>Looking for the Y-BOCS?</strong> The Yale–Brown
          Obsessive–Compulsive Scale is a copyrighted instrument and is no
          longer available on this website. Clinicians and researchers
          interested in the Y-BOCS or its derivatives can request it directly
          from the copyright holder at{' '}
          <a href='mailto:dr.g@ocdscales.com' style={{ color: '#9ad0e0' }}>
            dr.g@ocdscales.com
          </a>{' '}
          or via{' '}
          <a
            href='https://ocdscales.com'
            target='_blank'
            rel='noopener noreferrer'
            style={{ color: '#9ad0e0' }}
          >
            ocdscales.com
          </a>
          .
        </div>

        {/* Supportive framing note */}
        <div
          className='assessment-intro'
          style={{
            backgroundColor: '#2e2e2e',
            border: '1px solid #4a4a4a',
            borderLeft: '4px solid #17a2b8',
            borderRadius: '10px',
            padding: '18px 22px',
            margin: '20px auto',
            maxWidth: '700px',
            textAlign: 'left',
            fontSize: '0.95rem',
            lineHeight: '1.5',
          }}
        >
          <strong>Before you begin — a gentle note.</strong> This is a screening
          and self-tracking tool, not a diagnosis. Scores naturally rise and
          fall over time, so a single result is just one snapshot. It's most
          useful when shared with a therapist or doctor, who can help you make
          sense of it. Take it at your own pace, and feel free to pause if any
          question feels distressing.
        </div>

        {/* Historical scores */}
        <div className='assessment-intro'>
          <h2>Your Score History</h2>
          {currentScaleHistory.length === 0 ? (
            <p>
              Complete the assessment below and save it to start tracking your
              scores over time.
            </p>
          ) : (
            <>
              {!isLoggedIn && (
                <p style={{ fontSize: '0.9rem', color: '#cccccc' }}>
                  These results are stored only on this device and browser.
                  Create a free account to keep your history safely and view it
                  from any device.
                </p>
              )}
              <div style={{ width: '100%', height: 300 }}>
                <ResponsiveContainer width='100%' height='100%'>
                  <LineChart data={currentScaleHistory}>
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis
                      dataKey='timestamp'
                      tickFormatter={ts => new Date(ts).toLocaleDateString()}
                      tick={{ fontSize: 10, fill: 'white' }}
                    />
                    <YAxis
                      domain={[0, QUESTIONNAIRE.maxScore]}
                      tick={{ fontSize: 10, fill: 'white' }}
                    />
                    <Tooltip
                      contentStyle={{ backgroundColor: 'black', color: 'white' }}
                      labelFormatter={ts => new Date(ts).toDateString()}
                    />
                    <Legend />
                    <Line
                      type='monotone'
                      dataKey='totalScore'
                      stroke='#8884d8'
                      name={`${QUESTIONNAIRE.shortName} Score`}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {trend && (
                <p
                  style={{
                    textAlign: 'center',
                    fontSize: '1.05rem',
                    marginTop: '16px',
                    color:
                      trend.direction === 'down'
                        ? '#82ca9d'
                        : trend.direction === 'up'
                        ? '#e0a87a'
                        : '#cccccc',
                  }}
                >
                  {trend.direction === 'same'
                    ? 'Your latest score is the same as your previous assessment.'
                    : `Your latest score is ${Math.abs(trend.delta)} point${
                        Math.abs(trend.delta) === 1 ? '' : 's'
                      } ${
                        trend.direction === 'down' ? 'lower' : 'higher'
                      } than your previous assessment.`}
                </p>
              )}
            </>
          )}

          {legacyHistory.length > 0 && (
            <p style={{ fontSize: '0.85rem', color: '#cccccc', marginTop: '12px' }}>
              Note: your {legacyHistory.length} previously saved Y-BOCS result
              {legacyHistory.length === 1 ? ' is' : 's are'} still stored in
              your account. They use a different scoring range and are not
              shown on this chart.
            </p>
          )}
        </div>
        <br />

        {!scaleReady ? (
          /* Shown while the replacement questionnaire is awaiting permission. */
          <div
            className='assessment-intro'
            style={{
              backgroundColor: '#2e2e2e',
              border: '1px solid #4a4a4a',
              borderLeft: '4px solid #17a2b8',
              borderRadius: '10px',
              padding: '18px 22px',
              margin: '20px auto',
              maxWidth: '700px',
              textAlign: 'left',
              lineHeight: '1.5',
            }}
          >
            <strong>The screening questionnaire is temporarily offline.</strong>{' '}
            We are updating this page to a new validated screening instrument
            and it will be back shortly. Your previously saved results remain
            safe in your account. In the meantime, if you are concerned about
            OCD symptoms, speaking with a mental health professional is always
            the best step.
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <h2>Screening Questionnaire</h2>
            <p className='assessment-intro'>
              The following statements refer to experiences that many people
              have in their everyday lives. Select the answer that best
              describes <strong>how much that experience has distressed or
              bothered you during the past month</strong>.
            </p>

            {/* Progress indicator */}
            <div style={{ maxWidth: '500px', margin: '0 auto 24px auto' }}>
              <p style={{ marginBottom: '8px' }}>
                {answeredCount} of {QUESTIONNAIRE.items.length} questions answered
              </p>
              <div
                style={{
                  height: '10px',
                  backgroundColor: '#3a3a3a',
                  borderRadius: '6px',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${(answeredCount / QUESTIONNAIRE.items.length) * 100}%`,
                    backgroundColor: '#17a2b8',
                    transition: 'width 0.3s ease',
                  }}
                />
              </div>
            </div>

            {QUESTIONNAIRE.items.map((item, idx) => (
              <fieldset
                key={item.key}
                className='question-block'
                style={{ border: 'none', padding: 0, margin: '0 0 20px 0', minWidth: 0 }}
              >
                <legend style={{ padding: 0 }}>
                  <strong>
                    {idx + 1}. {item.text}
                  </strong>
                </legend>
                {QUESTIONNAIRE.responseOptions.map(option => (
                  <label key={option.value}>
                    <input
                      type='radio'
                      name={item.key}
                      value={option.value}
                      checked={answers[item.key] === option.value}
                      onChange={e => handleChange(e, item.key)}
                    />
                    {option.label}
                  </label>
                ))}
              </fieldset>
            ))}

            <br />
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                type='submit'
                style={{
                  backgroundColor: '#17a2b8',
                  color: 'white',
                  border: '1px solid #17a2b8',
                  borderRadius: '5px',
                  padding: '10px 20px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                }}
              >
                {currentUser ? 'Save Scores & Assessment' : 'Save My Results'}
              </button>
              <button
                type='button'
                onClick={handleReset}
                style={{
                  backgroundColor: 'transparent',
                  color: '#cccccc',
                  border: '1px solid #6c757d',
                  borderRadius: '5px',
                  padding: '10px 20px',
                  cursor: 'pointer',
                }}
              >
                Start over
              </button>
            </div>
            {!currentUser && (
              <p className='assessment-intro' style={{ fontSize: '0.85rem', marginTop: '10px' }}>
                Your results are saved on this device. Create a free account to
                back up your history and access it from any device.
              </p>
            )}
          </form>
        )}

        <br />
        {successMessage && (
          <p className='assessment-intro' style={{ color: '#82ca9d' }}>
            {successMessage}
          </p>
        )}

        {/* Results block */}
        {scaleReady && total > 0 && (
          <div
            style={{
              backgroundColor: '#2e2e2e',
              border: '1px solid #4a4a4a',
              borderLeft: '4px solid #17a2b8',
              borderRadius: '12px',
              padding: '24px',
              margin: '30px auto',
              maxWidth: '700px',
              textAlign: 'left',
              color: '#ffffff',
              lineHeight: '1.6',
            }}
          >
            <h2 style={{ marginTop: 0 }}>
              Your result: {total} / {QUESTIONNAIRE.maxScore} &mdash; {band.label}
            </h2>
            <p>{band.summary}</p>
            <p style={{ marginBottom: 0 }}>
              <strong>What you can do next:</strong> {band.nextSteps}
            </p>
            <button
              type='button'
              onClick={handleExport}
              style={{
                marginTop: '18px',
                backgroundColor: '#17a2b8',
                color: 'white',
                border: 'none',
                borderRadius: '25px',
                padding: '12px 24px',
                fontSize: '15px',
                fontWeight: 'bold',
                cursor: 'pointer',
              }}
            >
              🖨️ Download / print summary for my clinician
            </button>
          </div>
        )}

        {/* Safety / disclaimer block */}
        <div
          style={{
            backgroundColor: '#3a2e2e',
            border: '1px solid #6c4a4a',
            borderLeft: '4px solid #e08a8a',
            borderRadius: '12px',
            padding: '20px 24px',
            margin: '20px auto',
            maxWidth: '700px',
            textAlign: 'left',
            color: '#ffffff',
            fontSize: '0.95rem',
            lineHeight: '1.6',
          }}
        >
          <strong>A note on support.</strong> This tool is for screening and
          self-tracking. It cannot diagnose OCD and is not a substitute for
          professional care. If you are in crisis or thinking about harming
          yourself, please contact your local emergency services or a crisis
          line in your country right away. The directory at{' '}
          <a
            href='https://findahelpline.com'
            target='_blank'
            rel='noopener noreferrer'
            style={{ color: '#9ad0e0' }}
          >
            findahelpline.com
          </a>{' '}
          lists free, confidential helplines worldwide.
        </div>

        {/*
          NOTE: If the replacement scale's permission is granted only for
          NON-COMMERCIAL use, consider moving these two promotional blocks to a
          different page, or ask the copyright holder to confirm in writing
          that their presence on this page is acceptable.
        */}
        <MobileApps />
        <br />
        <BookPromo coverImage={bookCover} />

        {scaleReady && (
          <p
            className='assessment-intro'
            style={{ fontSize: '0.8rem', lineHeight: '1.2' }}
          >
            <strong>Acknowledgments:</strong> {QUESTIONNAIRE.citation} Used with
            permission of the copyright holder.
          </p>
        )}
      </div>
      <Footer />
    </>
  );
};

export default OCDScreeningForm;